import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DashboardSidebar } from '../components/DashboardSidebar';
import { SupportTicketPanel } from '../components/SupportTicketPanel';
import { Server, Users, DollarSign, Activity, Shield, HardDrive, PlusCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { CustomerService } from '../context/AppContext';
import { ServiceDetailCard } from '../components/ServiceDetailCard';
const chartData = [{
  name: 'Aug',
  spend: 0
}, {
  name: 'Sep',
  spend: 0
}, {
  name: 'Oct',
  spend: 0
}, {
  name: 'Nov',
  spend: 0
}, {
  name: 'Dec',
  spend: 0
}, {
  name: 'Jan',
  spend: 0
}];
export function BusinessDashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const {
    customerUser
  } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [services, setServices] = useState<CustomerService[]>([]);
  const [companyName, setCompanyName] = useState('Business');
  const [totalSpent, setTotalSpent] = useState(0);
  useEffect(() => {
    async function loadData() {
      if (!customerUser?.id) return;
      try {
        // Fetch customer record
        const {
          data: customerData,
          error: customerError
        } = await supabase.from('customers').select('services, company, name, total_spent').eq('id', customerUser.id).maybeSingle();
        if (customerError) throw customerError;
        if (customerData) {
          setCompanyName(customerData.company || customerData.name || 'Business');
          setTotalSpent(customerData.total_spent || 0);
          if (customerData.services) {
            setServices(customerData.services as CustomerService[]);
          }
        }
      } catch (error) {
        console.error('Error loading business dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [customerUser]);
  const activeServicesCount = services.filter(s => s.status === 'active').length;
  const monthlySpend = services.filter(s => s.status === 'active').reduce((sum, s) => {
    // Normalize to monthly spend
    return sum + (s.billingCycle === 'yearly' ? (s.price || 0) / 12 : s.price || 0);
  }, 0);
  const stats = [{
    label: 'Active Services',
    value: activeServicesCount.toString(),
    icon: Server,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10'
  }, {
    label: 'Team Members',
    value: '1',
    icon: Users,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10'
  }, {
    label: 'Monthly Spend',
    value: `$${monthlySpend.toFixed(2)}`,
    icon: DollarSign,
    color: 'text-fox-500',
    bg: 'bg-fox-500/10'
  }, {
    label: 'Uptime (30d)',
    value: '99.99%',
    icon: Activity,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10'
  }];
  const Overview = () => {
    if (isLoading) {
      return <div className="flex items-center justify-center h-64" data-id="element-2078">
          <div className="w-8 h-8 border-2 border-fox-500/30 border-t-fox-500 rounded-full animate-spin" data-id="element-2079" />
        </div>;
    }
    return <>
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4" data-id="element-2080">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} data-id="element-2081">
            <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-2082">
              {companyName} Infrastructure
            </h1>
            <p className="text-slate-400" data-id="element-2083">
              Enterprise Dashboard & Resource Management
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} data-id="element-2084">
            <Link to="/#services" className="inline-block bg-fox-600 hover:bg-fox-500 text-white px-5 py-2.5 rounded-lg font-medium transition-colors shadow-lg shadow-fox-500/20" data-id="element-2085">
              Deploy New Service
            </Link>
          </motion.div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-2086">
          {stats.map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-2087">
              <div className="flex items-center justify-between mb-4" data-id="element-2088">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg} ${stat.color}`} data-id="element-2089">
                  <stat.icon size={24} data-id="element-2090" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-white mb-1" data-id="element-2091">
                {stat.value}
              </h3>
              <p className="text-slate-400 text-sm font-medium" data-id="element-2092">{stat.label}</p>
            </motion.div>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2093">
          {/* Infrastructure List */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="lg:col-span-2 space-y-6" data-id="element-2094">
            <div className="flex items-center justify-between" data-id="element-2095">
              <h2 className="text-xl font-heading font-bold text-white" data-id="element-2096">
                Core Infrastructure
              </h2>
              <div className="flex gap-2" data-id="element-2097">
                <button className="p-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors" data-id="element-2098">
                  <Shield size={18} data-id="element-2099" />
                </button>
                <button className="p-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors" data-id="element-2100">
                  <HardDrive size={18} data-id="element-2101" />
                </button>
              </div>
            </div>

            <div className="space-y-4" data-id="element-2102">
              {services.length === 0 ? <div className="bg-slate-950 border border-slate-800 rounded-2xl p-12 text-center flex flex-col items-center justify-center" data-id="element-2103">
                  <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mb-4" data-id="element-2104">
                    <Server size={24} className="text-slate-500" data-id="element-2105" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-2106">
                    No infrastructure deployed
                  </h3>
                  <p className="text-slate-400 mb-6 max-w-sm" data-id="element-2107">
                    You don't have any active services yet. Deploy your first
                    server to get started.
                  </p>
                  <Link to="/#services" className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-5 py-2.5 rounded-lg font-medium transition-colors" data-id="element-2108">
                    <PlusCircle size={18} data-id="element-2109" />
                    Deploy Service
                  </Link>
                </div> : services.map((service, i) => <ServiceDetailCard key={i} service={service} index={i} data-id="element-2110" />)}
            </div>
          </motion.div>

          {/* Billing Chart */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="space-y-6" data-id="element-2111">
            <div className="flex items-center justify-between" data-id="element-2112">
              <h2 className="text-xl font-heading font-bold text-white" data-id="element-2113">
                Spend History
              </h2>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 h-[320px] flex flex-col" data-id="element-2114">
              <div className="mb-4" data-id="element-2115">
                <span className="text-3xl font-bold text-white" data-id="element-2116">
                  ${monthlySpend.toFixed(2)}
                </span>
                <span className="text-slate-400 text-sm ml-2" data-id="element-2117">
                  Current Month
                </span>
              </div>

              <div className="flex-1 w-full min-h-0" data-id="element-2118">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2119">
                  <BarChart data={chartData} margin={{
                  top: 10,
                  right: 0,
                  left: -20,
                  bottom: 0
                }} data-id="element-2120">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} data-id="element-2121" />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} data-id="element-2122" />
                    <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `$${value}`} data-id="element-2123" />
                    <Tooltip cursor={{
                    fill: '#1e293b'
                  }} contentStyle={{
                    backgroundColor: '#0f172a',
                    borderColor: '#1e293b',
                    borderRadius: '8px'
                  }} itemStyle={{
                    color: '#ea580c'
                  }} data-id="element-2124" />
                    <Bar dataKey="spend" fill="#ea580c" radius={[4, 4, 0, 0]} maxBarSize={40} data-id="element-2125" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>
        </div>
      </>;
  };
  return <div className="min-h-screen bg-slate-900 flex" data-id="element-2126">
      <DashboardSidebar type="business" isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} data-id="element-2127" />

      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0" data-id="element-2128">
        <div className="p-6 md:p-10 max-w-7xl mx-auto" data-id="element-2129">
          <Routes data-id="element-2130">
            <Route path="/" element={<Overview data-id="element-2132" />} data-id="element-2131" />
            <Route path="/support" element={<SupportTicketPanel userType="business" userName={companyName} userEmail={customerUser?.email || ''} data-id="element-2134" />} data-id="element-2133" />
            <Route path="*" element={<Navigate to="/dashboard/business" replace data-id="element-2136" />} data-id="element-2135" />
          </Routes>
        </div>
      </main>
    </div>;
}